import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { forceCollide, forceManyBody, forceLink, forceX, forceY } from "d3-force-3d";
import { Focus, Maximize2, Minus, Plus } from "lucide-react";
import type { AnalysisResult, GraphLink, GraphNode } from "../../server/types";
import { shortAddress } from "../lib/format";

interface BubbleMapProps {
  data: AnalysisResult;
  selected: GraphNode | null;
  hiddenClusters: Set<string>;
  onSelect: (node: GraphNode | null) => void;
}

type Positioned = GraphNode & { x?: number; y?: number };
type LinkEnd = string | Positioned;

const MIN_RADIUS = 4.5;
const MAX_RADIUS = 62;

function endpointId(value: LinkEnd): string {
  return typeof value === "string" ? value : value.id;
}

function endpointNode(value: LinkEnd): Positioned | null {
  return typeof value === "string" ? null : value;
}

/**
 * Bubble area tracks supply share, so the radius follows the square root.
 *
 * The scale is normalised against the largest *wallet*, not the largest address
 * overall. Burn addresses and pools routinely hold most of a supply — CAKE's
 * dead address alone holds 93% — and normalising against those would collapse
 * every real holder into an identical dot. Structural addresses still draw, they
 * just saturate at the top of the scale instead of setting it.
 */
function radiusScale(nodes: GraphNode[]) {
  const structural = (node: GraphNode) => node.kind === "burn" || node.kind === "pool";
  const wallets = nodes.filter((node) => !structural(node));
  const reference = Math.max(0.0001, ...(wallets.length ? wallets : nodes).map((node) => node.percentage));
  const root = Math.sqrt(reference);
  return (node: GraphNode) => {
    const share = Math.sqrt(Math.max(0, node.percentage)) / root;
    return Math.min(MAX_RADIUS, MIN_RADIUS + share * (MAX_RADIUS - MIN_RADIUS));
  };
}

function withAlpha(hex: string, alpha: number) {
  const value = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${value}`;
}

export function BubbleMap({ data, selected, hiddenClusters, onSelect }: BubbleMapProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const graph = useRef<any>(null);
  const [size, setSize] = useState({ width: 900, height: 700 });
  const [hovered, setHovered] = useState<GraphNode | null>(null);

  useEffect(() => {
    if (!wrapper.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(wrapper.current);
    return () => observer.disconnect();
  }, []);

  const graphData = useMemo(() => {
    const visible = data.nodes.filter((node) => !node.clusterId || !hiddenClusters.has(node.clusterId));
    const ids = new Set(visible.map((node) => node.id));
    return {
      nodes: visible.map((node) => ({ ...node })),
      links: data.links
        .filter((link) => ids.has(endpointId(link.source as LinkEnd)) && ids.has(endpointId(link.target as LinkEnd)))
        .map((link) => ({ ...link })),
    };
  }, [data, hiddenClusters]);

  const radiusOf = useMemo(() => radiusScale(graphData.nodes), [graphData.nodes]);

  /**
   * The default force-graph simulation lets bubbles sit on top of each other,
   * which is exactly why the map read as a random scatter. Collision at the drawn
   * radius, charge that scales with size, and link distances derived from the two
   * endpoint radii are what produce separated bubbles with visible edges.
   */
  useEffect(() => {
    const instance = graph.current;
    if (!instance) return;
    instance.d3Force(
      "collide",
      forceCollide((node: GraphNode) => radiusOf(node) + 3.5)
        .strength(1)
        .iterations(4),
    );
    // Repulsion has a floor so that a dense field of near-zero-balance wallets
    // still spreads out instead of collapsing into an unreadable hairball.
    instance.d3Force(
      "charge",
      forceManyBody().strength((node: GraphNode) => -34 - radiusOf(node) * 4.2),
    );
    const link = instance.d3Force("link") as ReturnType<typeof forceLink> | undefined;
    link
      ?.distance((edge: { source: LinkEnd; target: LinkEnd }) => {
        const source = endpointNode(edge.source);
        const target = endpointNode(edge.target);
        const span = (source ? radiusOf(source) : MIN_RADIUS) + (target ? radiusOf(target) : MIN_RADIUS);
        return span + 34;
      })
      .strength(0.16);
    // Gentle centring keeps disconnected bubbles in frame instead of drifting off.
    instance.d3Force("x", forceX(0).strength(0.045));
    instance.d3Force("y", forceY(0).strength(0.045));
    instance.d3ReheatSimulation?.();
  }, [graphData, radiusOf]);

  useEffect(() => {
    const timer = setTimeout(() => graph.current?.zoomToFit(700, 55), 900);
    return () => clearTimeout(timer);
  }, [graphData, size.width, size.height]);

  const highlighted = selected || hovered;
  const connectedIds = useMemo(() => {
    if (!highlighted) return new Set<string>();
    const ids = new Set<string>([highlighted.id]);
    for (const link of data.links) {
      const source = endpointId(link.source as LinkEnd);
      const target = endpointId(link.target as LinkEnd);
      if (source === highlighted.id) ids.add(target);
      if (target === highlighted.id) ids.add(source);
    }
    return ids;
  }, [data.links, highlighted]);

  const isLinkActive = useCallback(
    (link: GraphLink) =>
      Boolean(highlighted) &&
      connectedIds.has(endpointId(link.source as LinkEnd)) &&
      connectedIds.has(endpointId(link.target as LinkEnd)),
    [highlighted, connectedIds],
  );

  const maxWeight = useMemo(
    () => Math.max(0.0001, ...graphData.links.map((link) => link.weight || 0)),
    [graphData.links],
  );

  const nodeById = useMemo(
    () => new Map(graphData.nodes.map((node) => [node.id, node])),
    [graphData.nodes],
  );

  return (
    <div className="graph-wrap" ref={wrapper}>
      <ForceGraph2D
        ref={graph}
        width={size.width}
        height={size.height}
        graphData={graphData}
        backgroundColor="rgba(0,0,0,0)"
        nodeRelSize={1}
        warmupTicks={120}
        cooldownTicks={400}
        d3AlphaDecay={0.019}
        d3VelocityDecay={0.32}
        linkWidth={(raw) => {
          const link = raw as GraphLink;
          const scale = 0.55 + Math.sqrt((link.weight || 0) / maxWeight) * 2.6;
          return isLinkActive(link) ? scale + 1.5 : scale;
        }}
        linkColor={(raw) => {
          const link = raw as GraphLink;
          // An edge inherits its cluster colour, so a fan of transfers out of one
          // wallet reads as one coloured group rather than grey noise.
          const source = nodeById.get(endpointId(link.source as LinkEnd));
          const base = link.clusterId ? source?.color || "#7aa2ff" : "#6f83ad";
          if (isLinkActive(link)) return "#ffffff";
          if (highlighted) return withAlpha(base, 0.13);
          return withAlpha(base, link.clusterId ? 0.78 : 0.42);
        }}
        linkDirectionalArrowLength={(raw) => (isLinkActive(raw as GraphLink) ? 5.5 : 3)}
        linkDirectionalArrowRelPos={0.94}
        linkDirectionalArrowColor={(raw) =>
          isLinkActive(raw as GraphLink) ? "#ffffff" : "rgba(190,212,255,.45)"
        }
        linkDirectionalParticles={(raw) => (isLinkActive(raw as GraphLink) ? 2 : 0)}
        linkDirectionalParticleWidth={2.4}
        linkDirectionalParticleColor={() => "#ffffff"}
        nodeCanvasObject={(raw, ctx, globalScale) => {
          const node = raw as Positioned;
          if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
          const x = node.x as number;
          const y = node.y as number;
          const radius = radiusOf(node);
          const color = node.color || "#3f5586";
          const isFocused = highlighted?.id === node.id;
          const dimmed = Boolean(highlighted && !connectedIds.has(node.id));

          ctx.save();
          ctx.globalAlpha = dimmed ? 0.22 : 1;
          if (isFocused) {
            ctx.shadowColor = color;
            ctx.shadowBlur = 26;
          }
          const gradient = ctx.createRadialGradient(
            x - radius * 0.34,
            y - radius * 0.36,
            radius * 0.06,
            x,
            y,
            radius,
          );
          gradient.addColorStop(0, withAlpha(color, 0.98));
          gradient.addColorStop(0.55, withAlpha(color, 0.72));
          gradient.addColorStop(1, withAlpha(color, 0.24));
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
          ctx.shadowBlur = 0;

          ctx.lineWidth = isFocused ? 2.6 : node.clusterId ? 1.5 : 1;
          ctx.strokeStyle = isFocused ? "#ffffff" : withAlpha(color, node.clusterId ? 0.95 : 0.6);
          // A dashed ring marks an address that is code rather than a wallet.
          if (node.kind === "contract" || node.kind === "pool") ctx.setLineDash([3.5, 2.6]);
          ctx.stroke();
          ctx.setLineDash([]);

          if (node.kind === "creator") {
            ctx.beginPath();
            ctx.arc(x, y, radius + 3.5, 0, Math.PI * 2);
            ctx.lineWidth = 1.2;
            ctx.strokeStyle = "rgba(255,255,255,.8)";
            ctx.stroke();
          }
          // A wallet found by following transfers rather than by the holder index
          // gets a hollow centre, so inferred nodes never look like indexed ones.
          if (node.origin === "traced") {
            ctx.beginPath();
            ctx.arc(x, y, Math.max(1.4, radius * 0.24), 0, Math.PI * 2);
            ctx.fillStyle = "rgba(9,12,24,.72)";
            ctx.fill();
          }

          const pixels = radius * globalScale;
          if (pixels > 17 || isFocused) {
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const label = node.rank ? `#${node.rank}` : "•";
            const showShare = pixels > 34 && node.percentage >= 0.01;
            ctx.font = `700 ${Math.min(radius * 0.55, 13 / globalScale)}px Manrope, system-ui, sans-serif`;
            ctx.fillStyle = "rgba(255,255,255,.94)";
            ctx.fillText(label, x, showShare ? y - radius * 0.16 : y);
            if (showShare) {
              ctx.font = `600 ${Math.min(radius * 0.36, 10 / globalScale)}px "DM Mono", monospace`;
              ctx.fillStyle = "rgba(255,255,255,.72)";
              ctx.fillText(`${node.percentage.toFixed(1)}%`, x, y + radius * 0.32);
            }
          }
          ctx.restore();
        }}
        nodePointerAreaPaint={(raw, color, ctx) => {
          const node = raw as Positioned;
          if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x as number, node.y as number, radiusOf(node) + 2.5, 0, Math.PI * 2);
          ctx.fill();
        }}
        nodeLabel={(raw) => {
          const node = raw as GraphNode;
          const rank = node.rank ? `#${node.rank} ` : "";
          const origin =
            node.origin === "traced"
              ? "Reached by following transfers"
              : node.origin === "insider"
                ? "Indexed insider network"
                : "Indexed holder";
          const label = (node.label || shortAddress(node.address, 6)).replace(/[&<>"']/g, value => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[value]!));
          return `<div class="graph-tooltip"><b>${rank}${label}</b><span>${node.percentage.toFixed(
            3,
          )}% of supply · ${node.degree} link${node.degree === 1 ? "" : "s"}</span><span>${origin}</span></div>`;
        }}
        onNodeHover={(node) => setHovered((node as GraphNode | null) || null)}
        onNodeClick={(raw) => {
          const node = raw as Positioned;
          onSelect(node);
          if (typeof node.x === "number" && typeof node.y === "number") {
            graph.current?.centerAt(node.x, node.y, 500);
            graph.current?.zoom(Math.max(graph.current?.zoom() || 1, 2.2), 500);
          }
        }}
        onBackgroundClick={() => onSelect(null)}
      />
      <div className="map-legend">
        <span><i className="legend-bubble legend-bubble--cluster" /> Cluster</span>
        <span><i className="legend-bubble legend-bubble--pool" /> Pool</span>
        <span><i className="legend-bubble legend-bubble--creator" /> Creator</span>
        <span><i className="legend-bubble legend-bubble--traced" /> Traced wallet</span>
        <span><i className="legend-line" /> Token transfer</span>
      </div>
      <div className="map-controls">
        <button title="Zoom in" onClick={() => graph.current?.zoom((graph.current?.zoom() || 1) * 1.35, 250)}><Plus /></button>
        <button title="Zoom out" onClick={() => graph.current?.zoom((graph.current?.zoom() || 1) / 1.35, 250)}><Minus /></button>
        <button title="Fit map" onClick={() => graph.current?.zoomToFit(500, 55)}><Focus /></button>
        <button title="Fullscreen" onClick={() => wrapper.current?.requestFullscreen?.()}><Maximize2 /></button>
      </div>
    </div>
  );
}
